import * as React from 'react';
import { Button, makeStyles, Text, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        paddingTop: '56px',
        paddingBottom: '64px',
        paddingLeft: '20px',
        paddingRight: '20px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusXLarge,
        border: `1px dashed ${tokens.colorNeutralStroke1}`,
    },
    icon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '72px',
        height: '72px',
        marginBottom: '16px',
        borderRadius: tokens.borderRadiusCircular,
        color: tokens.colorBrandForeground1,
        backgroundColor: tokens.colorBrandBackground2,
        '& svg': { width: '32px', height: '32px' },
    },
    title: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: '6px',
    },
    description: {
        color: tokens.colorNeutralForeground3,
        maxWidth: '420px',
        lineHeight: tokens.lineHeightBase300,
        marginBottom: '20px',
    },
    actions: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
});

export interface IEmptyStateProps {
    icon: React.ReactNode;
    title: string;
    description?: string;
    actions?: React.ReactNode;
    /** Shortcut for a single primary action. */
    action?: { label: string; onClick: () => void; icon?: React.ReactElement };
}

const EmptyState: React.FC<IEmptyStateProps> = ({ icon, title, description, actions, action }) => {
    const styles = useStyles();
    return (
        <div className={styles.root} role="status">
            <div className={styles.icon} aria-hidden>{icon}</div>
            <Text className={styles.title} as="h2" block>{title}</Text>
            {description && <Text className={styles.description} block>{description}</Text>}
            {(actions || action) && (
                <div className={styles.actions}>
                    {actions}
                    {action && (
                        <Button appearance="primary" icon={action.icon} onClick={action.onClick}>
                            {action.label}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmptyState;
